import React from "react";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, Plus, Pencil, CheckCircle2, AlertCircle } from "lucide-react";
import {
  EquipmentDto,
  MaintenanceAreaDto,
  MaintenanceTaskDto,
  equipmentApi,
  maintenanceAreasApi,
  maintenanceTasksApi,
} from "../../../services/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EquipmentFormDialog } from "./EquipmentFormDialog";
import { TaskFormDialog } from "../tasks/TaskFormDialog";
import { CompleteTaskDialog } from "../tasks/CompleteTaskDialog";
import { TaskHistoryList } from "../tasks/TaskHistoryList";
import {
  CATEGORY_LABELS, PRIORITY_LABELS, describeRecurrence, describeReminders, formatDate, isOverdue,
  intToCategory, intToPriority,
} from "../utils";

export const EquipmentDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [equipment, setEquipment] = React.useState<EquipmentDto | null>(null);
  const [tasks, setTasks] = React.useState<MaintenanceTaskDto[]>([]);
  const [areas, setAreas] = React.useState<MaintenanceAreaDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [editingEquipment, setEditingEquipment] = React.useState(false);
  const [creatingTask, setCreatingTask] = React.useState(false);
  const [editingTask, setEditingTask] = React.useState<MaintenanceTaskDto | null>(null);
  const [completing, setCompleting] = React.useState<MaintenanceTaskDto | null>(null);
  const [historyVisibleFor, setHistoryVisibleFor] = React.useState<string | null>(null);
  const [historyKey, setHistoryKey] = React.useState(0);

  const reload = React.useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const eq = await equipmentApi.getBySlug(slug);
      const [ts, a] = await Promise.all([
        maintenanceTasksApi.search({ equipmentId: eq.id, includeInactive: true }),
        maintenanceAreasApi.getAll(false),
      ]);
      setEquipment(eq);
      setTasks(ts);
      setAreas(a);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  React.useEffect(() => { void reload(); }, [reload]);

  if (loading) return <div className="text-sm text-slate-500">Cargando…</div>;
  if (error || !equipment) {
    return (
      <div className="space-y-2">
        <Button variant="outline" onClick={() => navigate("/mantenimiento/equipos")}>
          <ArrowLeft className="size-4 mr-2" /> Volver
        </Button>
        <div className="rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
          {error ?? "Equipo no encontrado"}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate("/mantenimiento/equipos")}>
          <ArrowLeft className="size-4 mr-2" /> Volver
        </Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => setEditingEquipment(true)}>
            <Pencil className="size-4 mr-2" /> Editar equipo
          </Button>
          <Button onClick={() => setCreatingTask(true)}>
            <Plus className="size-4 mr-2" /> Nueva tarea
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          <div>
            <h2 className="text-lg font-bold">{equipment.name}</h2>
            <div className="text-sm text-slate-600">
              {equipment.areaName}
              {equipment.code && <> · {equipment.code}</>}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-sm ml-auto">
            {equipment.type && <div><span className="text-slate-500">Tipo:</span> {equipment.type}</div>}
            {equipment.brand && <div><span className="text-slate-500">Marca:</span> {equipment.brand}</div>}
            {equipment.model && <div><span className="text-slate-500">Modelo:</span> {equipment.model}</div>}
            {equipment.serialNumber && <div><span className="text-slate-500">Nº serie:</span> {equipment.serialNumber}</div>}
            {equipment.purchaseDate && <div><span className="text-slate-500">Compra:</span> {formatDate(equipment.purchaseDate, false)}</div>}
            {equipment.location && <div><span className="text-slate-500">Ubicación:</span> {equipment.location}</div>}
          </div>
        </div>
        {equipment.notes && (
          <p className="text-sm text-slate-600 mt-3 whitespace-pre-wrap">{equipment.notes}</p>
        )}
      </Card>

      <h3 className="font-semibold pt-2">Tareas</h3>

      {tasks.length === 0 ? (
        <Card className="p-6 text-center text-slate-500 text-sm">
          Aún no hay tareas en este equipo. Crea la primera.
        </Card>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => {
            const overdue = isOverdue(t);
            const showHistory = historyVisibleFor === t.id;
            return (
              <Card key={t.id} className={`p-4 ${overdue ? "border-red-300 bg-red-50/40" : ""} ${!t.isActive ? "opacity-60" : ""}`}>
                <div className="flex flex-wrap gap-3 items-start">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2">
                      {overdue && <AlertCircle className="size-4 text-red-600" />}
                      <h4 className="font-semibold">{t.title}</h4>
                      {!t.isActive && <span className="text-xs rounded bg-slate-200 px-2 py-0.5">Inactiva</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                      <span><strong>Categoría:</strong> {CATEGORY_LABELS[intToCategory(t.category)]}</span>
                      <span><strong>Prioridad:</strong> {PRIORITY_LABELS[intToPriority(t.priority)]}</span>
                      <span><strong>Recurrencia:</strong> {describeRecurrence(t)}</span>
                      <span><strong>Avisos:</strong> {describeReminders(t)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Próximo</div>
                    <div className={`font-semibold ${overdue ? "text-red-700" : ""}`}>{formatDate(t.nextDueAt, false)}</div>
                    {t.lastCompletedAt && (
                      <div className="text-xs text-slate-500 mt-1">Última: {formatDate(t.lastCompletedAt, false)}</div>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t">
                  <Button size="sm" onClick={() => setCompleting(t)} disabled={!t.isActive}>
                    <CheckCircle2 className="size-4 mr-1.5" /> Marcar realizada
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingTask(t)}>
                    <Pencil className="size-3.5 mr-1.5" /> Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto"
                    onClick={() => setHistoryVisibleFor(showHistory ? null : t.id)}
                  >
                    {showHistory ? "Ocultar historial" : "Ver historial"}
                  </Button>
                </div>

                {showHistory && (
                  <div className="mt-3 pt-3 border-t">
                    <TaskHistoryList taskId={t.id} refreshKey={historyKey} />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {editingEquipment && (
        <EquipmentFormDialog
          equipment={equipment}
          areas={areas}
          onClose={() => setEditingEquipment(false)}
          onSaved={async () => {
            setEditingEquipment(false);
            // El slug puede haber cambiado si se renombró el equipo: re-resolvemos por id
            // y, si el slug nuevo difiere del actual, navegamos a la URL nueva.
            try {
              const refreshed = await equipmentApi.getById(equipment.id);
              if (refreshed.slug !== slug) {
                navigate(`/mantenimiento/equipos/${refreshed.slug}`, { replace: true });
                return;
              }
            } catch { /* ignore */ }
            void reload();
          }}
        />
      )}
      {(creatingTask || editingTask) && (
        <TaskFormDialog
          task={editingTask}
          defaultEquipmentId={equipment.id}
          onClose={() => { setCreatingTask(false); setEditingTask(null); }}
          onSaved={() => { setCreatingTask(false); setEditingTask(null); void reload(); }}
        />
      )}
      {completing && (
        <CompleteTaskDialog
          task={completing}
          onClose={() => setCompleting(null)}
          onSaved={() => {
            setCompleting(null);
            setHistoryKey((k) => k + 1);
            void reload();
          }}
        />
      )}
    </div>
  );
};
